import type { TOC } from '@ember/component/template-only';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import EmberTooltip from 'ember-tooltips/components/ember-tooltip';
import { eq } from 'ember-truth-helpers';

interface RelatedEvent {
  eventId: string;
  processedDateDisplay: string;
  processedTimeDisplay: string;
  integrationName: string;
  operationTypeLabel: string;
  operationStatus: string;
  failureType: string | null;
  durationMillis: number;
  correlationId: string | null;
}

interface RelatedEventsDrawerSignature {
  Args: {
    events: RelatedEvent[];
    correlationId: string;
    onClose: () => void;
    onOpenEventDetail: (event: RelatedEvent) => void;
  };
}

export default <template>
  <div class="!my-0 top-0 right-0 z-[998] fixed bg-light-grey">
    <div class="w-[548px] max-w-[548px] h-screen overflow-auto border-box shadow-lg">
      {{! Header }}
      <div class="flex justify-between items-start p-6 border-b border-gray-100">
        <div class="min-w-0">
          <h3 class="text-base font-semibold mb-0.5">Related Events</h3>
          <p class="text-xs text-carbon-40 mb-0 font-mono break-all">{{@correlationId}}</p>
        </div>
        <button type="button" class="text-carbon-40 hover:text-carbon-70 mt-1 shrink-0 ml-4" {{on "click" @onClose}}>
          {{svgJar "cross-black" height="16" width="16"}}
        </button>
      </div>

      {{! Table }}
      {{#if (eq @events.length 0)}}
        <div class="flex items-center justify-center py-12 text-xs text-carbon-30">No related events found</div>
      {{else}}
        <table class="w-full text-sm border-collapse">
          <thead>
            <tr class="border-b border-gray-100">
              <th class="text-left text-xs text-carbon-40 font-medium px-6 py-3 whitespace-nowrap">Date</th>
              <th class="text-left text-xs text-carbon-40 font-medium px-3 py-3 whitespace-nowrap">Integration</th>
              <th class="text-left text-xs text-carbon-40 font-medium px-3 py-3 whitespace-nowrap">Outcome</th>
              <th class="text-right text-xs text-carbon-40 font-medium px-3 py-3 whitespace-nowrap">Duration</th>
              <th class="text-left text-xs text-carbon-40 font-medium px-3 py-3 whitespace-nowrap">Details</th>
            </tr>
          </thead>
          <tbody>
            {{#each @events as |event|}}
              <tr
                class="border-b border-gray-50
                  {{if (eq event.operationStatus 'OK') 'cursor-default' 'cursor-pointer hover:bg-carbon-5'}}"
                {{on "click" (fn @onOpenEventDetail event)}}
              >
                <td class="px-6 py-3 text-xs text-carbon-70 whitespace-nowrap">
                  {{event.processedDateDisplay}}<br />
                  <span class="text-carbon-40">{{event.processedTimeDisplay}}</span>
                </td>
                <td class="px-3 py-3 text-xs text-carbon-70 max-w-[120px] truncate">{{event.integrationName}}</td>
                <td class="px-3 py-3 text-xs">
                  {{#if (eq event.operationStatus "OK")}}
                    <span class="flex items-center gap-1 text-green-700 font-medium">
                      <div class="shrink-0 w-3.5 h-3.5">{{svgJar "checkmark-circle-green" height="14" width="14"}}</div>
                      Success
                    </span>
                  {{else if (eq event.operationStatus "FAILED")}}
                    {{#if (eq event.failureType "TIMEOUT")}}
                      <span class="flex items-center gap-1 text-yellow-600 font-medium">
                        <div class="shrink-0 w-3.5 h-3.5">{{svgJar "caution-triangle" height="14" width="14"}}</div>
                        Timeout
                      </span>
                    {{else}}
                      <span class="flex items-center gap-1 text-red-600 font-medium">
                        <div class="shrink-0 w-3.5 h-3.5">{{svgJar "circle-cross-red" height="14" width="14"}}</div>
                        Failed
                      </span>
                    {{/if}}
                  {{else if (eq event.operationStatus "PARTIAL")}}
                    <span class="flex items-center gap-1 text-yellow-600 font-medium">
                      <div class="shrink-0 w-3.5 h-3.5">{{svgJar "caution-triangle" height="14" width="14"}}</div>
                      Partial
                    </span>
                  {{/if}}
                </td>
                <td
                  class="px-3 py-3 text-xs text-carbon-70 text-right whitespace-nowrap"
                >{{event.durationMillis}}ms</td>
                <td class="px-3 py-3 text-xs">
                  {{#if (eq event.operationStatus "OK")}}
                    <div class="inline-flex cursor-not-allowed text-carbon-20 opacity-40">
                      <div class="shrink-0 w-3.5 h-3.5">{{svgJar "chevron-grey-right" height="14" width="14"}}</div>
                      <EmberTooltip @side="top" @text="This event has no errors" />
                    </div>
                  {{else}}
                    <button
                      type="button"
                      class="flex items-center gap-1 text-carbon-50 hover:text-carbon-70"
                      {{on "click" (fn @onOpenEventDetail event)}}
                    >
                      <div class="shrink-0 w-3.5 h-3.5">{{svgJar "chevron-grey-right" height="14" width="14"}}</div>
                    </button>
                  {{/if}}
                </td>
              </tr>
            {{/each}}
          </tbody>
        </table>
      {{/if}}
    </div>
  </div>
</template> satisfies TOC<RelatedEventsDrawerSignature>;
